import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  claimManagers: [],
  managersFromSameCompany: [],
  selected_claimManager: null,
  loading: false,       // mutation operations (add / update / delete / block)
  fetchLoading: false,  // fetch operations
  hasFetched: false,    // prevents redundant re-fetches on remount
  recordsTotal: 0,
  errors: []
};

const normalizeErrors = (payload) =>
  Array.isArray(payload?.message)
    ? payload.message
    : [payload?.message].filter(Boolean);

export const fetchManagersFromSameCompany = createAsyncThunk(
  'claimManager/fetchManagersFromSameCompany',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/ClaimManager/getallClaimManagers');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addClaimManager = createAsyncThunk(
  'claimManager/addClaimManager',
  async ({ email, phone, password, firstname, lastname, services }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post('/claimManager', { email, phone, password, firstname, lastname, services });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteClaimManager = createAsyncThunk(
  'claimManager/deleteClaimManager',
  async ({ ClaimManagerId }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/claimManager/${ClaimManagerId}`);
      return ClaimManagerId;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const blockClaimManager = createAsyncThunk(
  'claimManager/blockClaimManager',
  async ({ ClaimManagerId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/claimManager/block/${ClaimManagerId}`);
      return { id: ClaimManagerId, data: response.data };
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateClaimManager = createAsyncThunk(
  'claimManager/updateClaimManager',
  async ({ ClaimManagerId, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/claimManager/${ClaimManagerId}`, data);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const claimManagerSlice = createSlice({
  name: 'claimManager',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchManagersFromSameCompany
      .addCase(fetchManagersFromSameCompany.pending, (state) => { state.fetchLoading = true; })
      .addCase(fetchManagersFromSameCompany.fulfilled, (state, { payload }) => {
        state.managersFromSameCompany = payload || [];
        state.fetchLoading = false;
        state.hasFetched = true;
      })
      .addCase(fetchManagersFromSameCompany.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload);
        state.fetchLoading = false;
      })

      // addClaimManager
      .addCase(addClaimManager.pending, (state) => { state.loading = true; })
      .addCase(addClaimManager.fulfilled, (state, { payload }) => {
        const newManager = { ...payload, _DT_RowId: payload._id };
        state.claimManagers.push(newManager);
        state.managersFromSameCompany.push(newManager);
        state.loading = false;
      })
      .addCase(addClaimManager.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload);
        state.loading = false;
      })

      // deleteClaimManager
      .addCase(deleteClaimManager.pending, (state) => { state.loading = true; })
      .addCase(deleteClaimManager.fulfilled, (state, { payload: id }) => {
        state.managersFromSameCompany = state.managersFromSameCompany.filter((m) => m._id !== id);
        state.claimManagers = state.claimManagers.filter((m) => m._id !== id);
        state.loading = false;
      })
      .addCase(deleteClaimManager.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload);
        state.loading = false;
      })

      // blockClaimManager — prefer server response, fall back to toggle
      .addCase(blockClaimManager.pending, (state) => { state.loading = true; })
      .addCase(blockClaimManager.fulfilled, (state, { payload }) => {
        const idx = state.managersFromSameCompany.findIndex((m) => m._id === payload.id);
        if (idx !== -1) {
          state.managersFromSameCompany[idx].locked =
            payload.data?.locked !== undefined
              ? payload.data.locked
              : !state.managersFromSameCompany[idx].locked;
        }
        state.loading = false;
      })
      .addCase(blockClaimManager.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload);
        state.loading = false;
      })

      // updateClaimManager
      .addCase(updateClaimManager.pending, (state) => { state.loading = true; })
      .addCase(updateClaimManager.fulfilled, (state, { payload }) => {
        const idx = state.managersFromSameCompany.findIndex((m) => m._id === payload._id);
        if (idx !== -1) {
          state.managersFromSameCompany[idx] = { ...state.managersFromSameCompany[idx], ...payload };
        }
        state.loading = false;
      })
      .addCase(updateClaimManager.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload);
        state.loading = false;
      });
  }
});

export default claimManagerSlice.reducer;
